import React, { useState, useEffect } from 'react';
import { css } from 'emotion';
import { space } from '@guardian/src-foundations';
import { CommentCount } from '@frontend/web/components/CommentCount';
import { RightColumn } from '@frontend/web/components/RightColumn';
import { AdSlot } from '@root/src/web/components/AdSlot';
import { namedAdSlotParameters } from '@root/src/model/advertisement';
import { App as Comments } from '@guardian/discussion-rendering';
import { from } from '@guardian/src-foundations/mq';
import { Portal } from '@frontend/web/components/Portal';
import { Lazy } from '@frontend/web/components/Lazy';
import { Flex } from '@frontend/web/components/Flex';
import { SignedInAs } from '@frontend/web/components/SignedInAs';
import { ContainerLayout } from '@frontend/web/components/ContainerLayout';
import { Hide } from '@frontend/web/components/Hide';
import { getDiscussion } from '@root/src/web/lib/getDiscussion';
import { getCommentContext } from '@root/src/web/lib/getCommentContext';
const commentIdFromUrl = () => {
    if (typeof window === 'undefined')
        return;
    const { hash } = window.location;
    if (!hash)
        return;
    if (!hash.includes('comment'))
        return;
    if (!hash.split('-')[1])
        return;
    return parseInt(hash.split('-')[1], 10);
};
export const Discussion = ({ discussionApiUrl, shortUrlId, isCommentable, pillar, user, discussionD2Uid, discussionApiClientHeader, enableDiscussionSwitch, isAdFreeUser, shouldHideAds, beingHydrated, }) => {
    const [commentCount, setCommentCount] = useState();
    const [isClosedForComments, setIsClosedForComments] = useState(true);
    const [commentPage, setCommentPage] = useState();
    const [commentPageSize, setCommentPageSize] = useState();
    const [commentOrderBy, setCommentOrderBy] = useState();
    const [isExpanded, setIsExpanded] = useState(false);
    const [hashCommentId, setHashCommentId] = useState(commentIdFromUrl());
    const hasCommentsHash = typeof window !== 'undefined' &&
        window.location &&
        window.location.hash === '#comments';
    const handlePermalink = (commentId) => {
        if (typeof window === 'undefined')
            return false;
        window.location.hash = `#comment-${commentId}`;
        const comment = window.document.getElementById(`comment-${commentId}`);
        if (comment) {
            // The comment was already on the page so just scroll to it.
            comment.scrollIntoView();
        }
        setHashCommentId(commentId);
        return false;
    };
    useEffect(() => {
        const callFetch = async () => {
            const response = await getDiscussion(discussionApiUrl, shortUrlId);
            setCommentCount(response && response.discussion.commentCount);
            setIsClosedForComments(response && response.discussion.isClosedForComments);
        };
        if (isCommentable) {
            callFetch();
        }
    }, [discussionApiUrl, shortUrlId, isCommentable]);
    // Check the url to see if there is a comment hash, e.g. ...crisis#comment-139113120
    // If so, make a call to get the context of this comment so we know what page it is
    // on.
    useEffect(() => {
        if (hashCommentId) {
            getCommentContext(discussionApiUrl, hashCommentId).then((context) => {
                setCommentPage(context.page);
                setCommentPageSize(context.pageSize);
                setCommentOrderBy(context.orderBy);
                setIsExpanded(true);
            });
        }
    }, [discussionApiUrl, hashCommentId]);
    useEffect(() => {
        if (hasCommentsHash) {
            setIsExpanded(true);
        }
    }, [hasCommentsHash]);
    const hideAd = isAdFreeUser || shouldHideAds;
    return (React.createElement(React.Fragment, null,
        commentCount !== undefined && beingHydrated && (React.createElement(Portal, { root: "comment-count-root" },
            React.createElement(CommentCount, { isCommentable: isCommentable, commentCount: commentCount, pillar: pillar, setIsExpanded: setIsExpanded }))),
        React.createElement(ContainerLayout, { padSides: false, padContent: false, 
            // If we're not hiding an advert stretch to the right
            stretchRight: !hideAd, leftContent: 
            // eslint-disable-next-line react/jsx-wrap-multilines
            React.createElement(SignedInAs, { pillar: pillar, enableDiscussionSwitch: enableDiscussionSwitch, user: user, commentCount: commentCount || 0, isClosedForComments: isClosedForComments }) },
            React.createElement(Flex, null,
                React.createElement("div", { className: css `
                            ${from.leftCol} {
                                padding-left: 10px;
                            }
                        ` },
                    React.createElement(Hide, { when: "above", breakpoint: "leftCol" },
                        React.createElement("div", { className: css `
                                    padding-bottom: ${space[2]}px;
                                ` },
                            React.createElement(SignedInAs, { pillar: pillar, enableDiscussionSwitch: enableDiscussionSwitch, user: user, commentCount: commentCount || 0 }))),
                    beingHydrated && isExpanded && (React.createElement(Comments, { user: user, baseUrl: discussionApiUrl, pillar: pillar, initialPage: commentPage, pageSizeOverride: commentPageSize, isClosedForComments: isClosedForComments ||
                            !enableDiscussionSwitch, orderByOverride: commentOrderBy, shortUrl: shortUrlId, additionalHeaders: {
                            'D2-X-UID': discussionD2Uid,
                            'GU-Client': discussionApiClientHeader,
                        }, expanded: isExpanded, commentToScrollTo: hashCommentId, onPermalinkClick: handlePermalink, apiKey: "dotcom-rendering", onHeightChange: () => { } })),
                    beingHydrated && !isExpanded && (React.createElement(Lazy, { margin: 300 },
                        React.createElement(Comments, { user: user, baseUrl: discussionApiUrl, pillar: pillar, initialPage: commentPage, pageSizeOverride: commentPageSize, isClosedForComments: isClosedForComments ||
                                !enableDiscussionSwitch, orderByOverride: commentOrderBy, shortUrl: shortUrlId, additionalHeaders: {
                                'D2-X-UID': discussionD2Uid,
                                'GU-Client': discussionApiClientHeader,
                            }, expanded: isExpanded, commentToScrollTo: hashCommentId, onPermalinkClick: handlePermalink, apiKey: "dotcom-rendering", onHeightChange: () => { } })))),
                React.createElement(React.Fragment, null, !hideAd && (React.createElement(RightColumn, null,
                    React.createElement("div", { className: css `
                                        position: static;
                                        height: 100%;
                                        padding-left: 20px;
                                    ` },
                        React.createElement(AdSlot, { asps: namedAdSlotParameters('comments'), isSticky: true })))))))));
};
